/* Shared workshop hero. Initial content is immediate; only user/autoplay slide changes transition. */
(() => {
  'use strict';
  if (window.MRWorkshopHero) return;
  window.MRWorkshopHero = true;
  const states = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function hydrate(slide) {
    slide.querySelectorAll('source[data-srcset]').forEach(source => {
      source.srcset = source.dataset.srcset;
      delete source.dataset.srcset;
    });
    const image = slide.querySelector('img');
    if (!image) return Promise.resolve();
    image.loading = 'eager';
    if (image.dataset.srcset) { image.srcset = image.dataset.srcset; delete image.dataset.srcset; }
    if (image.dataset.src) { image.src = image.dataset.src; delete image.dataset.src; }
    if (image.complete && image.naturalWidth) return Promise.resolve();
    if (typeof image.decode === 'function') return image.decode();
    return new Promise((resolve, reject) => {
      const clean = () => { image.removeEventListener('load', loaded); image.removeEventListener('error', failed); };
      const loaded = () => { clean(); resolve(); };
      const failed = () => { clean(); reject(new Error('Hero photograph could not load')); };
      image.addEventListener('load', loaded, { once: true });
      image.addEventListener('error', failed, { once: true });
    });
  }

  function initialize(root) {
    if (states.has(root)) return;
    const abort = new AbortController();
    const opts = { signal: abort.signal };
    const cleaners = [];
    states.set(root, () => { abort.abort(); cleaners.forEach(clean => clean()); states.delete(root); });

    root.querySelectorAll('[data-mr-style1-slider], [data-mr-style2-slider]').forEach(slider => {
      const style1 = slider.hasAttribute('data-mr-style1-slider');
      const slides = Array.from(slider.querySelectorAll(style1 ? '[data-mr-style1-slide]' : '[data-mr-style2-slide]'));
      const dots = Array.from(slider.querySelectorAll(style1 ? '[data-mr-style1-dot]' : '[data-mr-style2-dot]'));
      if (slides.length < 2) return; // The current workshop banners are static.
      let current = 0, requested = 0, ticket = 0, timer = 0, warmTimer = 0;
      let visible = false, hovering = false, focused = false, touching = false, touch = null;
      const speed = Math.max(parseInt(slider.dataset.speed || '5000', 10), 2000);
      const modulo = index => ((index % slides.length) + slides.length) % slides.length;
      const stop = () => { clearTimeout(timer); clearTimeout(warmTimer); timer = 0; warmTimer = 0; };
      const canRun = () => slider.dataset.autoplay === 'true' && !reduceMotion.matches && visible && !document.hidden && !hovering && !focused && !touching;
      const schedule = () => {
        stop();
        if (!canRun()) return;
        const connection = navigator.connection;
        if (!connection?.saveData && !/(^|-)2g$/.test(connection?.effectiveType || '')) {
          warmTimer = setTimeout(() => hydrate(slides[modulo(current + 1)]).catch(() => {}), speed * 0.65);
        }
        timer = setTimeout(() => show(current + 1), speed);
      };
      const show = async index => {
        const next = modulo(index), request = ++ticket;
        requested = next;
        stop();
        if (next === current) { schedule(); return; }
        try { await hydrate(slides[next]); } catch (_) { if (request === ticket) schedule(); return; }
        if (request !== ticket || !root.isConnected) return;
        current = next;
        slides.forEach((slide, i) => { slide.classList.toggle('is-active', i === current); slide.setAttribute('aria-hidden', String(i !== current)); });
        dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === current); dot.setAttribute('aria-current', String(i === current)); });
        schedule();
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i), opts));
      slider.addEventListener('mouseenter', () => { hovering = true; stop(); }, opts);
      slider.addEventListener('mouseleave', () => { hovering = false; schedule(); }, opts);
      slider.addEventListener('focusin', () => { focused = true; stop(); }, opts);
      slider.addEventListener('focusout', event => { if (!slider.contains(event.relatedTarget)) { focused = false; schedule(); } }, opts);
      slider.addEventListener('touchstart', event => {
        touch = event.touches[0] ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
        touching = true; stop();
      }, { ...opts, passive: true });
      slider.addEventListener('touchend', event => {
        const end = event.changedTouches[0]; touching = false;
        if (touch && end && Math.abs(end.clientX - touch.x) > 45 && Math.abs(end.clientX - touch.x) > Math.abs(end.clientY - touch.y)) show(requested + (end.clientX < touch.x ? 1 : -1));
        else schedule();
        touch = null;
      }, { ...opts, passive: true });
      slider.addEventListener('touchcancel', () => { touch = null; touching = false; schedule(); }, opts);
      document.addEventListener('visibilitychange', schedule, opts);
      reduceMotion.addEventListener?.('change', schedule, opts);
      document.addEventListener('shopify:block:select', event => {
        const selected = event.target.closest?.(style1 ? '[data-mr-style1-slide]' : '[data-mr-style2-slide]');
        if (selected && slider.contains(selected)) { focused = true; show(Number(selected.dataset.index) || 0); }
      }, opts);
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        entries.forEach(entry => { visible = entry.isIntersecting; schedule(); });
      }) : null;
      if (observer) observer.observe(slider); else { visible = true; schedule(); }
      cleaners.push(() => { ticket++; stop(); observer?.disconnect(); });
    });

    // Read only the tour's booking attributes, not every mutation on the storefront.
    const badge = root.querySelector('[data-mr-hero-availability]');
    const reserve = root.querySelector('[data-mr-mobile-reserve]');
    const pdf = root.querySelector('[data-mr-mobile-pdf]');
    const reserveLabel = root.querySelector('[data-mr-mobile-reserve-label]');
    const pdfLabel = root.querySelector('[data-mr-mobile-pdf-label]');
    const original = { badge: badge?.textContent.trim() || '', reserve: reserve?.getAttribute('href') || '', pdf: pdf?.getAttribute('href') || '', reserveLabel: reserveLabel?.textContent.trim() || '', pdfLabel: pdfLabel?.textContent.trim() || '' };
    let contentObserver = null, content = null, frame = 0;
    const setAction = (node, labelNode, label, href) => {
      if (!node) return;
      node.hidden = !label;
      if (labelNode && labelNode.textContent.trim() !== label) labelNode.textContent = label;
      if (href) { if (node.getAttribute('href') !== href) node.setAttribute('href', href); }
      else node.removeAttribute('href');
    };
    const synchronize = () => {
      frame = 0;
      if (!root.isConnected || (!reserve && !pdf)) return;
      const found = document.querySelector('[data-ctc-section]');
      if (found !== content) {
        contentObserver?.disconnect(); content = found;
        if (content && 'MutationObserver' in window) {
          contentObserver = new MutationObserver(requestSync);
          contentObserver.observe(content, { attributes: true, attributeFilter: ['data-ctc-reserve-color','data-ctc-status-color','data-ctc-reserve-link','data-ctc-pdf-link','data-ctc-reserve-label','data-ctc-pdf-label','data-ctc-status-badge'] });
        }
      }
      const d = content?.dataset;
      setAction(reserve, reserveLabel, content ? (d.ctcReserveLabel || '') : original.reserveLabel, content ? (d.ctcReserveLink || '') : original.reserve);
      setAction(pdf, pdfLabel, content ? (d.ctcPdfLabel || '') : original.pdfLabel, content ? (d.ctcPdfLink || '') : original.pdf);
      if (badge) { const text = content ? (d.ctcStatusBadge || '') : original.badge; badge.hidden = !text; if (badge.textContent.trim() !== text) badge.textContent = text; }
      if (d?.ctcReserveColor) root.style.setProperty('--mr-mobile-reserve-color', d.ctcReserveColor);
      if (d?.ctcStatusColor) root.style.setProperty('--mr-mobile-status-color', d.ctcStatusColor);
    };
    function requestSync() { if (!frame) frame = requestAnimationFrame(synchronize); }
    document.addEventListener('shopify:section:load', requestSync, opts);
    document.addEventListener('shopify:section:unload', requestSync, opts);
    requestSync();
    cleaners.push(() => { contentObserver?.disconnect(); cancelAnimationFrame(frame); });

    const media = root.querySelector('.mr-hero-media');
    const details = media?.querySelector('.mr-hero-details');
    if (details) {
      let fitFrame = 0, height = -1;
      const fit = () => {
        fitFrame = 0;
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const next = Math.ceil(details.getBoundingClientRect().height);
        if (next !== height) { height = next; media.style.setProperty('--mr-mobile-detail-height', `${height}px`); }
      };
      const requestFit = () => { if (!fitFrame) fitFrame = requestAnimationFrame(fit); };
      const observer = 'ResizeObserver' in window ? new ResizeObserver(requestFit) : null;
      observer?.observe(details);
      window.addEventListener('resize', requestFit, { ...opts, passive: true });
      window.addEventListener('load', requestFit, { ...opts, once: true });
      requestFit();
      cleaners.push(() => { observer?.disconnect(); cancelAnimationFrame(fitFrame); });
    }
  }
  const start = scope => {
    if (scope.matches?.('[data-section-type="custom-tour-hero"]')) initialize(scope);
    scope.querySelectorAll('[data-section-type="custom-tour-hero"]').forEach(initialize);
  };
  start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-section-type="custom-tour-hero"]').forEach(root => states.get(root)?.());
  });
})();
